import React, { useEffect, useContext } from 'react';
import styled from 'styled-components';
import scrollama from 'scrollama';
import color, { RegulationActionColor } from '../../styles/color';
import MapContext from '../../hooks/MapContext';
import MapInteractive from './MapInteractive';
import CountryDetail from './CountryDetail';
import BarChart from './BarChart';
import { device } from '../../styles/size';

import regulationSteps from '../../copy/regulation.yml';
import casestudySteps from '../../copy/casestudy.yml';
import plasticwasteSteps from '../../copy/plasticwaste.yml';

const Article = styled.article`
  pointer-events: none;
  z-index: 100;
  position: relative;

  @media ${device.tablet} {
    z-index: 0;
  }

  & * {
    pointer-events: none;
  }

  & #plasticwaste1 {
    padding-top: 50vh;
  }
`;

const StepContainer = styled.div`
  padding-top: 20vh;
  padding-bottom: 60vh;

  @media ${device.tablet} {
    padding-top: 0;
    padding-bottom: 20vh;
  }
`;

const TextBox = styled.div`
  width: 60vw;
  max-width: 550px;
  margin: 0 auto;
  padding: 10px 20px;
  background-color: rgba(255, 255, 255, 0.75);
  border: 1px dotted ${color.ippd};
  font-size: 20px;
  position: relative;
  pointer-events: all;

  @media ${device.tablet} {
    width: 90vw;
    margin: 0 auto;
  }
`;

const TransparentBox = styled(TextBox)`
  margin-left: 10vw;
  width: 40vw;
  background-color: rgba(255, 255, 255, 0.75);
`;

const MapTextBox = styled(TextBox)`
  margin-left: 50vw;
  width: 40vw;
  background-color: rgba(255, 255, 255, 0.75);
`;
const RightTextBox = styled(TextBox)`
  margin-left: 50vw;
  width: 40vw;
  background-color: transparent;
  border: none;
`;

const Heading1 = styled.h3`
  font-size: 30px;
  margin: 10px 0 -20px 0;
  text-align: left;
`;

const Heading2 = styled.h4`
  font-size: 24px;
  margin: 5px 0;
`;

const Action = styled.span`
  display: inline;
  background-color: ${({ action }) => RegulationActionColor(action)};
  color: white;
  padding: 0 7px;
`;

function Format(text, i) {
  if (text.type === 'text') {
    return text.content;
  } else if (text.type === 'nowrap') {
    return (
      <span key={i} style={{ whiteSpace: 'nowrap' }}>
        {text.content}
      </span>
    );
  } else if (text.type === 'link') {
    return (
      <a key={text.content} href={text.url}>
        {text.content}
      </a>
    );
  } else if (text.type === 'action') {
    return (
      <Action action={text.action} key={i}>
        {text.content}
      </Action>
    );
  } else if (text.type === 'map-interactive') {
    return <MapInteractive key={text.content} />;
  } else if (text.type === 'country-detail') {
    return <CountryDetail key={i} content={text.contents} />;
  } else if (text.type === 'country-detail-with-bar') {
    return (
      <React.Fragment key={i}>
        <CountryDetail content={text.contents} />
        <BarChart
          dataset={[
            { phase: 'ก่อน', plasticuse: 45000 },
            { phase: 'หลัง', plasticuse: 31500 },
          ]}
        />
      </React.Fragment>
    );
  } else if (text.type === 'flag') {
    return <img src={text.src} width="50%" key={i} />;
  } else if (text.type === 'heading1') {
    return <Heading1 key={i}>{text.content}</Heading1>;
  } else if (text.type === 'heading2') {
    return <Heading2 key={i}>{text.content}</Heading2>;
  } else if (text.type === 'list') {
    return (
      <ul key={i}>
        {text.listitem.map((content, i) => (
          <li key={i}>{content}</li>
        ))}
      </ul>
    );
  }
}

export { Format };

const PlasticwasteStep = React.memo(({ step, text }) => {
  return (
    <StepContainer className="article--step" data-step={step} id={step}>
      <TransparentBox>{text.map(Format)}</TransparentBox>
    </StepContainer>
  );
});

const RegulationStep = React.memo(({ step, text }) => {
  if (['regulation1', 'regulation2', 'regulationTHA1'].includes(step)) {
    return (
      <StepContainer className="article--step" data-step={step}>
        <MapTextBox>{text.map(Format)}</MapTextBox>
      </StepContainer>
    );
  } else {
    return (
      <StepContainer className="article--step" data-step={step}>
        <TextBox>{text.map(Format)}</TextBox>
      </StepContainer>
    );
  }
});

const CasestudyStep = React.memo(({ step, text }) => {
  return (
    <StepContainer className="article--step" data-step={step}>
      <RightTextBox>{text.map(Format)}</RightTextBox>
    </StepContainer>
  );
});

const MapArticle = () => {
  const { setStep, year } = useContext(MapContext);

  useEffect(() => {
    const scroller = scrollama();
    scroller
      .setup({
        step: '.article--step',
        offset: 0.7,
        debug: false,
      })
      .onStepEnter(setStep);

    window.addEventListener('resize', () => scroller.resize());
    return () => scroller.destroy();
  }, []);

  return (
    <Article>
      {plasticwasteSteps.map((step) => (
        <PlasticwasteStep key={step.step} {...step} />
      ))}
      {regulationSteps.map((step) => (
        <RegulationStep key={step.step} {...step} />
      ))}
      {casestudySteps.map((step) => (
        <CasestudyStep key={step.step} {...step} />
      ))}
    </Article>
  );
};

export default React.memo(MapArticle);
